import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { Icon, Skeleton, WidgetCard } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { patientModel } from '@/entities/patient';

import styles from './ClinicalCaseDescription.module.scss';

type ClinicalCaseDescriptionProps = {
  patientID: string;
  className?: string;
  testID?: string;
};

export const ClinicalCaseDescription: FC<ClinicalCaseDescriptionProps> = (
  props,
) => {
  const { className, testID, patientID } = props;

  const dispatch = useAppDispatch();
  const { isMobile } = useMedia();

  const patientLoading = useAppSelector(patientModel.selectors.selectLoading);
  const { CanEditClinicalCaseDescription, CanViewClinicalCaseDescription } =
    useAppSelector(
      patientModel.selectors.selectPatientYourPermissions(patientID),
    );

  const clinicalCaseDescription = useAppSelector(
    patientModel.selectors.selectClinicalCaseDescription,
  );

  if (patientLoading === 'pending') {
    return <Skeleton width="100%" height="148px" borderRadius="16px" />;
  }

  if (!CanViewClinicalCaseDescription) {
    return null;
  }

  const openDescriptionEditorModalHandler = () => {
    if (CanEditClinicalCaseDescription) {
      dispatch(
        modalModel.actions.openModal({
          modalID: ModalID.ClinicalCaseDescriptionEditorModal,
          data: {
            patientID,
          },
        }),
      );
    }
  };

  return (
    <WidgetCard
      className={cn(styles.container, className)}
      data-testid={testID}
      data-component="clinical-case-description"
    >
      <div className={cn(styles.title, isMobile ? 'h4m' : 'h4')}>
        <FormattedMessage
          id="patientInfo.clinicalCaseDescription"
          defaultMessage="Clinical case description"
        />

        {CanEditClinicalCaseDescription && (
          <Icon
            name="pen"
            size={isMobile ? 24 : 34}
            className={styles.editIcon}
            onClick={openDescriptionEditorModalHandler}
          />
        )}
      </div>

      {clinicalCaseDescription ? (
        <div className={cn(styles.description, isMobile ? 'p3' : 'p2')}>
          {clinicalCaseDescription}
        </div>
      ) : (
        <Icon
          name="doc"
          size={48}
          className={cn(
            styles.addIcon,
            !CanEditClinicalCaseDescription && styles.cursorAuto,
          )}
          onClick={openDescriptionEditorModalHandler}
        />
      )}
    </WidgetCard>
  );
};
